<template>
  <div class="q-pa-sm">
    <div
      v-for="source in allowedSources"
      :key="source.field"
      class="q-mb-md col-6"
    >
      <span class="bold">
        {{ source.label }}
      </span>

      <div v-if="filters">
        <div class="row">
          <FilterSourceInput
            v-model="filters[source.classField].includedSources"
            action="include"
            class="full-width q-mb-sm"
            :disable="!!filters[source.classField].excludedSources.length"
            placeholder="Include"
            :resource="source.resource"
            :scope="source.field"
            :target-type="getTargetType(source)"
            :text-types="textTypes"
            @update:model-value="updateFilters"
          />
          <div class="row q-ma-sm">
            <QBtn
              v-if="filters[source.classField].includedSources.length"
              class="clipboard-button"
              color="primary"
              dense
              icon="file_copy"
              size="sm"
              @click="
                copySourceNamesToClipboard(
                  filters[source.classField].includedSources
                )
              "
            >
              <StreemTooltip> Copy source names to clipboard </StreemTooltip>
            </QBtn>
            <QBtn
              v-if="filters[source.classField].includedSources.length"
              class="q-ml-sm clipboard-button"
              :color="source.field"
              dense
              icon="file_copy"
              size="sm"
              @click="
                copyTagsToClipboard(filters[source.classField].includedSources)
              "
            >
              <StreemTooltip> Copy tags to clipboard </StreemTooltip>
            </QBtn>
          </div>
        </div>
      </div>
      <div
        v-if="!noExclusions"
        class="row"
      >
        <FilterSourceInput
          v-model="filters[source.classField].excludedSources"
          action="exclude"
          class="full-width q-mb-sm"
          :disable="!!filters[source.classField].includedSources.length"
          placeholder="Exclude"
          :resource="source.resource"
          :scope="source.field"
          :target-type="getTargetType(source)"
          :text-types="textTypes"
          @update:model-value="updateFilters"
        />
        <div class="row q-ma-sm">
          <QBtn
            v-if="filters[source.classField].excludedSources.length"
            class="clipboard-button"
            color="primary"
            dense
            icon="file_copy"
            size="sm"
            @click="
              copySourceNamesToClipboard(
                filters[source.classField].excludedSources
              )
            "
          >
            <StreemTooltip> Copy source names to clipboard </StreemTooltip>
          </QBtn>
          <QBtn
            v-if="filters[source.classField].excludedSources.length"
            class="q-ml-sm clipboard-button"
            :color="source.field"
            dense
            icon="file_copy"
            size="sm"
            @click="
              copyTagsToClipboard(filters[source.classField].excludedSources)
            "
          >
            <StreemTooltip> Copy tags to clipboard </StreemTooltip>
          </QBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import useClipboard from "shared/composables/useClipboard";
import { getSourceByClass } from "shared/helpers/media";
import { constantize } from "shared/helpers/string";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";

import FilterSourceInput from "src/components/sources/FilterSourceInput.vue";

export default {
  name: "FilterSourceTab",
  components: {
    FilterSourceInput,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    sourceType: {
      type: String,
      required: true,
    },
    conditionFilter: {
      type: String,
      required: true,
    },
    conditionField: {
      type: String,
      required: true,
    },
    textTypes: {
      type: Array,
      required: true,
    },
    sources: {
      type: Array,
      default: () => [],
    },
    noExclusions: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const organisationFeaturesStore = useOrganisationFeaturesStore();

    const { enabledSources } = storeToRefs(organisationFeaturesStore);

    const { copyToClipboard } = useClipboard();

    return { copyToClipboard, enabledSources };
  },
  computed: {
    allowedSources() {
      const sources = this.sources.length ? this.sources : this.enabledSources;

      return sources.filter(({ field }) => field !== "social");
    },
    filters: {
      get() {
        return this.buildFilters();
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  methods: {
    buildFilters() {
      return [
        "website",
        "newspaper",
        "magazine",
        "tv_channel",
        "radio_station",
        "podcast",
      ].reduce((acc, type) => {
        const source = getSourceByClass(type);
        let condition = source[this.conditionField];
        if (this.sourceType !== "Group") condition = constantize(condition);

        acc[type] = {
          includedSources: this.mapFilters(source, "include", condition),
          excludedSources: this.mapFilters(source, "exclude", condition),
        };

        return acc;
      }, {});
    },
    mapFilters(source, action, condition) {
      return this.modelValue
        .filter(
          (filter) =>
            filter.action === action &&
            filter[this.conditionFilter] === condition
        )
        .map((filter) => ({
          ...filter,
          text: this.textTypes
            .reduce(
              (acc, key) => `${acc} ${filter[key] ? filter[key] : ""} - `,
              ""
            )
            .trim()
            .replace(/-$/, ""),
          classes: `tag-${source.field}`,
        }));
    },
    updateFilters() {
      const filters = Object.values(this.filters).reduce(
        (acc, filter) => [
          ...acc,
          ...filter.includedSources,
          ...filter.excludedSources,
        ],
        []
      );

      this.$emit("update:modelValue", filters);
    },
    getTargetType(source) {
      return this.sourceType === "Group"
        ? "SourceGroup"
        : constantize(source.classField);
    },
    copyTagsToClipboard(tags) {
      this.copyToClipboard(JSON.stringify(tags));
    },
    copySourceNamesToClipboard(tags) {
      this.copyToClipboard(tags.map((tag) => tag.name).toLocaleString());
    },
  },
};
</script>

<style lang="scss" scoped>
h4 {
  margin: 0;
}

.clipboard-button {
  height: fit-content;
}
</style>
